import { useCallback, useEffect, useState } from "react";
import type { AttachmentDTO } from "@justone/shared";
import { Dialog, Icon } from "./ui";

export function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)}KB`;
  return `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)}MB`;
}

const extOf = (name: string) => (name.includes(".") ? name.split(".").pop()!.slice(0, 4).toUpperCase() : "파일");

/** 사진 하나는 원래 비율로(최대 360×300), 여러 장은 정사각형 바둑판 */
function displaySize(a: AttachmentDTO): { width: number; height: number } {
  const w = a.width ?? 320;
  const h = a.height ?? 240;
  const scale = Math.min(1, 360 / w, 300 / h);
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

export function AttachmentList({ attachments }: { attachments: AttachmentDTO[] }) {
  const [viewing, setViewing] = useState<number | null>(null);
  const images = attachments.filter((a) => a.isImage && a.thumbUrl);
  const files = attachments.filter((a) => !(a.isImage && a.thumbUrl));
  const single = images.length === 1;

  return (
    <div className="attachments">
      {images.length > 0 && (
        <div className={`att-images${single ? " single" : ""}`}>
          {images.map((a, i) => {
            const size = single ? displaySize(a) : { width: 120, height: 120 };
            return (
              <button key={a.id} type="button" className="att-image" style={size} onClick={() => setViewing(i)} aria-label={`${a.filename} 크게 보기`}>
                <img src={a.thumbUrl!} alt={a.filename} width={size.width} height={size.height} loading="lazy" decoding="async" />
              </button>
            );
          })}
        </div>
      )}
      {files.map((a) => (
        <a key={a.id} className="att-file" href={`${a.url}?download`} download={a.filename}>
          <span className="ext">{extOf(a.filename)}</span>
          <span className="meta">
            <span className="name">{a.filename}</span>
            <span className="size">{formatBytes(a.size)}</span>
          </span>
          <Icon name="arrowDown" label="내려받기" />
        </a>
      ))}
      {viewing !== null && <Lightbox images={images} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function Lightbox({ images, index, onIndex, onClose }: { images: AttachmentDTO[]; index: number; onIndex: (i: number) => void; onClose: () => void }) {
  const a = images[index]!;
  const go = useCallback((d: number) => onIndex((index + d + images.length) % images.length), [index, images.length, onIndex]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [go]);
  return (
    <Dialog open onClose={onClose} label="사진 보기">
      <div className="lightbox">
        <div className="lightbox-bar">
          <span className="name">{a.filename}</span>
          {images.length > 1 && (
            <span className="pos">
              {index + 1} / {images.length}
            </span>
          )}
          <a className="btn btn-sm" href={`${a.url}?download`} download={a.filename}>
            내려받기
          </a>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="닫기">
            <Icon name="close" />
          </button>
        </div>
        <img src={a.url} alt={a.filename} />
        {images.length > 1 && (
          <>
            <button type="button" className="lightbox-nav prev" onClick={() => go(-1)} aria-label="이전 사진">
              ‹
            </button>
            <button type="button" className="lightbox-nav next" onClick={() => go(1)} aria-label="다음 사진">
              ›
            </button>
          </>
        )}
      </div>
    </Dialog>
  );
}
